import React, { Suspense, lazy, useEffect } from 'react';
import { Route, Switch, useLocation } from 'wouter';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from '@/components/ui/toaster';
import MainLayout from '@/components/Layout/MainLayout';
import { AuthProvider } from '@/contexts/AuthContext';
import { EnvironmentProvider } from '@/contexts/EnvironmentContext';
import { ScopeProvider } from '@/contexts/ScopeContext';
import { BotLifecycleProvider } from '@/contexts/BotLifecycleContext';
import { AppInitializer } from '@/components/AppInitializer';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { StrategyLibraryAdminGate } from '@/routes/AdminRoutes';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import './index.css';

// Eager pages
import Login from '@/pages/Login';
import AuthCallback from '@/pages/AuthCallback';
import SilentCallback from '@/pages/SilentCallback';
import Overview from '@/pages/Overview';
import NotFound from '@/pages/NotFound';

// Lazy pages
const Strategies = lazy(() => import('@/pages/Strategies'));
const StrategyDetail = lazy(() => import('@/pages/StrategyDetail'));
const Connections = lazy(() => import('@/pages/Venues'));
const Compliance = lazy(() => import('@/pages/Compliance'));
const Reports = lazy(() => import('@/pages/Reports'));
const Backtesting = lazy(() => import('@/pages/Backtesting'));
const Monitoring = lazy(() => import('@/pages/Monitoring'));
const Settings = lazy(() => import('@/pages/Settings'));
const Profile = lazy(() => import('@/pages/Profile'));
const ApiKeys = lazy(() => import('@/pages/ApiKeys'));
const Organization = lazy(() => import('@/pages/Organization'));
const Admin = lazy(() => import('@/pages/Admin'));
const UserDetail = lazy(() => import('@/pages/UserDetail'));
const Help = lazy(() => import('@/pages/Help'));
const Status = lazy(() => import('@/pages/Status'));
const InternalChecks = lazy(() => import('@/pages/InternalChecks'));
const ConsolePage = lazy(() => import('@/pages/Console'));
const Tenants = lazy(() => import('@/pages/Tenants'));
const TenantDetail = lazy(() => import('@/pages/TenantDetail'));
const CreateTenant = lazy(() => import('@/pages/CreateTenant'));
const TaxCenter = lazy(() => import('@/pages/TaxCenter/TaxCenter'));
const DebugModal = lazy(() => import('@/pages/DebugModal'));
const KPIDialogDebug = lazy(() => import('@/pages/KPIDialogDebug'));
const KPIDialogLayoutDebug = lazy(() => import('@/pages/KPIDialogLayoutDebug'));
const Notifications = lazy(() => import('@/pages/Notifications'));
const AdminStrategyLibrary = lazy(() => import('@/pages/AdminStrategyLibrary'));
const ServerError = lazy(() => import('@/pages/ServerError'));
const Agents = lazy(() => import('@/pages/Agents'));
const AgentDetail = lazy(() => import('@/pages/AgentDetail'));

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600" />
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    },
  },
});

const RedirectToAgents = () => {
  const [, setLocation] = useLocation();
  useEffect(() => {
    const search = typeof window !== 'undefined' ? window.location.search : '';
    setLocation(`/agents${search}`);
  }, [setLocation]);
  return null;
};

const RedirectToConnections = () => {
  const [, setLocation] = useLocation();
  useEffect(() => {
    setLocation('/connections');
  }, [setLocation]);
  return null;
};

const RedirectToReports = () => {
  const [, setLocation] = useLocation();
  useEffect(() => {
    setLocation('/reports');
  }, [setLocation]);
  return null;
};
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <EnvironmentProvider>
        <AuthProvider>
          <ScopeProvider>
            <BotLifecycleProvider>
            <AppInitializer>
              <ErrorBoundary level="page">
              <Switch>
                <Route path="/" component={Login} />
                <Route path="/login" component={Login} />
                <Route path="/auth/callback" component={AuthCallback} />
                <Route path="/auth/silent-callback" component={SilentCallback} />

                <Route path="/overview">
                  <ProtectedRoute>
                    <MainLayout>
                      <Overview />
                    </MainLayout>
                  </ProtectedRoute>
                </Route>

                <Route path="/notifications">
                  <ProtectedRoute>
                    <MainLayout>
                      <Suspense fallback={<PageLoader />}>
                        <Notifications />
                      </Suspense>
                    </MainLayout>
                  </ProtectedRoute>
                </Route>

                <Route path="/agents/:agentId">
                  <ProtectedRoute permission="view_bots">
                    <MainLayout>
                      <Suspense fallback={<PageLoader />}>
                        <AgentDetail />
                      </Suspense>
                    </MainLayout>
                  </ProtectedRoute>
                </Route>

                <Route path="/agents">
                  <ProtectedRoute permission="view_bots">
                    <MainLayout>
                      <Suspense fallback={<PageLoader />}>
                        <Agents />
                      </Suspense>
                    </MainLayout>
                  </ProtectedRoute>
                </Route>

                <Route path="/automations" component={RedirectToAgents} />
                <Route path="/trading" component={RedirectToAgents} />
                <Route path="/bots" component={RedirectToAgents} />

                <Route path="/console">
                  <ProtectedRoute>
                    <MainLayout>
                      <Suspense fallback={<PageLoader />}>
                        <ConsolePage />
                      </Suspense>
                    </MainLayout>
                  </ProtectedRoute>
                </Route>

                <Route path="/strategies">
                  <ProtectedRoute permission="view_strategies">
                    <MainLayout>
                      <Suspense fallback={<PageLoader />}>
                        <Strategies />
                      </Suspense>
                    </MainLayout>
                  </ProtectedRoute>
                </Route>

                <Route path="/strategies/:id">
                  <ProtectedRoute permission="view_strategies">
                    <MainLayout>
                      <Suspense fallback={<PageLoader />}>
                        <StrategyDetail />
                      </Suspense>
                    </MainLayout>
                  </ProtectedRoute>
                </Route>

                <Route path="/connections">
                  <ProtectedRoute permission="view_venues">
                    <MainLayout>
                      <Suspense fallback={<PageLoader />}>
                        <Connections />
                      </Suspense>
                    </MainLayout>
                  </ProtectedRoute>
                </Route>

                <Route path="/venues" component={RedirectToConnections} />

                <Route path="/compliance">
                  <ProtectedRoute permission="view_compliance">
                    <MainLayout>
                      <Suspense fallback={<PageLoader />}>
                        <Compliance />
                      </Suspense>
                    </MainLayout>
                  </ProtectedRoute>
                </Route>

                <Route path="/reports">
                  <ProtectedRoute permission="view_reports">
                    <MainLayout>
                      <Suspense fallback={<PageLoader />}>
                        <Reports />
                      </Suspense>
                    </MainLayout>
                  </ProtectedRoute>
                </Route>

                <Route path="/analytics" component={RedirectToReports} />

                <Route path="/tax">
                  <ProtectedRoute
                    permission="view_reports"
                    roles={['org_owner', 'rugira_platform_admin', 'auditor', 'rugira_compliance_admin']}
                  >
                    <MainLayout>
                      <Suspense fallback={<PageLoader />}>
                        <TaxCenter />
                      </Suspense>
                    </MainLayout>
                  </ProtectedRoute>
                </Route>

                <Route path="/backtesting">
                  <ProtectedRoute permission="view_strategies">
                    <MainLayout>
                      <Suspense fallback={<PageLoader />}>
                        <Backtesting />
                      </Suspense>
                    </MainLayout>
                  </ProtectedRoute>
                </Route>

                <Route path="/monitoring">
                  <ProtectedRoute permission="view_monitoring">
                    <MainLayout>
                      <Suspense fallback={<PageLoader />}>
                        <Monitoring />
                      </Suspense>
                    </MainLayout>
                  </ProtectedRoute>
                </Route>

                <Route path="/settings">
                  <ProtectedRoute>
                    <MainLayout>
                      <Suspense fallback={<PageLoader />}>
                        <Settings />
                      </Suspense>
                    </MainLayout>
                  </ProtectedRoute>
                </Route>

                <Route path="/profile">
                  <ProtectedRoute>
                    <MainLayout>
                      <Suspense fallback={<PageLoader />}>
                        <Profile />
                      </Suspense>
                    </MainLayout>
                  </ProtectedRoute>
                </Route>

                <Route path="/debug-modal">
                  <Suspense fallback={<PageLoader />}>
                    <DebugModal />
                  </Suspense>
                </Route>

                <Route path="/debug-kpi-modal">
                  <Suspense fallback={<PageLoader />}>
                    <KPIDialogDebug />
                  </Suspense>
                </Route>

                <Route path="/debug-kpi-layout">
                  <Suspense fallback={<PageLoader />}>
                    <KPIDialogLayoutDebug />
                  </Suspense>
                </Route>

                <Route path="/admin">
                  <ProtectedRoute permission="view_admin">
                    <MainLayout>
                      <Suspense fallback={<PageLoader />}>
                        <Admin />
                      </Suspense>
                    </MainLayout>
                  </ProtectedRoute>
                </Route>

                <Route path="/admin/strategy-library">
                  <ProtectedRoute permission="view_admin">
                    <StrategyLibraryAdminGate>
                      <MainLayout>
                        <Suspense fallback={<PageLoader />}>
                          <AdminStrategyLibrary />
                        </Suspense>
                      </MainLayout>
                    </StrategyLibraryAdminGate>
                  </ProtectedRoute>
                </Route>

                <Route path="/admin/user/:id">
                  <ProtectedRoute permission="view_admin">
                    <MainLayout>
                      <Suspense fallback={<PageLoader />}>
                        <UserDetail />
                      </Suspense>
                    </MainLayout>
                  </ProtectedRoute>
                </Route>

                <Route path="/tenants">
                  <ProtectedRoute permission="view_admin" roles={['org_owner', 'rugira_platform_admin']}>
                    <MainLayout>
                      <Suspense fallback={<PageLoader />}>
                        <Tenants />
                      </Suspense>
                    </MainLayout>
                  </ProtectedRoute>
                </Route>

                <Route path="/tenants/new">
                  <ProtectedRoute permission="view_admin" roles={['org_owner', 'rugira_platform_admin']}>
                    <MainLayout>
                      <Suspense fallback={<PageLoader />}>
                        <CreateTenant />
                      </Suspense>
                    </MainLayout>
                  </ProtectedRoute>
                </Route>

                <Route path="/tenants/:id">
                  <ProtectedRoute permission="view_admin" roles={['org_owner', 'rugira_platform_admin']}>
                    <MainLayout>
                      <Suspense fallback={<PageLoader />}>
                        <TenantDetail />
                      </Suspense>
                    </MainLayout>
                  </ProtectedRoute>
                </Route>

                <Route path="/api-keys">
                  <ProtectedRoute>
                    <MainLayout>
                      <Suspense fallback={<PageLoader />}>
                        <ApiKeys />
                      </Suspense>
                    </MainLayout>
                  </ProtectedRoute>
                </Route>

                <Route path="/organization">
                  <ProtectedRoute>
                    <MainLayout>
                      <Suspense fallback={<PageLoader />}>
                        <Organization />
                      </Suspense>
                    </MainLayout>
                  </ProtectedRoute>
                </Route>

                <Route path="/help">
                  <ProtectedRoute>
                    <MainLayout>
                      <Suspense fallback={<PageLoader />}>
                        <Help />
                      </Suspense>
                    </MainLayout>
                  </ProtectedRoute>
                </Route>

                <Route path="/status">
                  <ProtectedRoute>
                    <MainLayout>
                      <Suspense fallback={<PageLoader />}>
                        <Status />
                      </Suspense>
                    </MainLayout>
                  </ProtectedRoute>
                </Route>

                <Route path="/internal/checks">
                  <ProtectedRoute>
                    <MainLayout>
                      <Suspense fallback={<PageLoader />}>
                        <InternalChecks />
                      </Suspense>
                    </MainLayout>
                  </ProtectedRoute>
                </Route>

                <Route path="/debug-modal">
                  <Suspense fallback={<PageLoader />}>
                    <DebugModal />
                  </Suspense>
                </Route>

                <Route path="/500">
                  <Suspense fallback={<PageLoader />}>
                    <ServerError />
                  </Suspense>
                </Route>

                <Route component={NotFound} />
              </Switch>
              </ErrorBoundary>
            </AppInitializer>
            </BotLifecycleProvider>
          </ScopeProvider>
        </AuthProvider>
      </EnvironmentProvider>
      <Toaster />
    </QueryClientProvider>
  );
}

export default App;
